import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Search as SearchIcon, Loader2, Lock, Crown, ArrowRight, Command as CmdIcon } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { VAULT } from "../data/vaultStats";

/**
 * CommandPalette — Cmd-K / Ctrl-K global prompt search.
 * Mounted once at the app root (inside ProtectedRoute area). Opens on Cmd/Ctrl+K,
 * fuzzy-searches every vault prompt + Elite Track via GET /api/search/prompts.
 * Keyboard: ↑↓ to move, Enter to open, Esc to close.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Global Cmd/Ctrl+K listener
  useEffect(() => {
    function onKey(e) {
      const isK = e.key === "k" || e.key === "K";
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQuery("");
      setResults([]);
      setActive(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/search/prompts", { params: { q, limit: 40 } });
        setResults(r.data?.results || []);
        setActive(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open]);

  const goToResult = useCallback(
    (r) => {
      if (!r) return;
      setOpen(false);
      if (r.kind === "elite") {
        navigate("/vault/elite");
      } else {
        navigate(`/vault/${r.track_slug}#p-${r.prompt_index}`);
      }
    },
    [navigate],
  );

  function onKeyDownList(e) {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      goToResult(results[active]);
    }
  }

  // Scroll active into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!user) return null; // Palette is a member-only utility

  return (
    <>
      {/* Bottom-left floating hint (subtle, dismissable) */}
      {!open && <PaletteHint onOpen={() => setOpen(true)} />}
      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24 px-4 animate-in fade-in duration-150"
          onClick={() => setOpen(false)}
          data-testid="command-palette"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-xl bg-[#0a0a0a] border border-purple-500/40 shadow-[0_0_60px_rgba(168,85,247,0.25)] animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onKeyDownList}
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-900">
              <Search size={16} className="text-purple-400 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${VAULT.promptsFmt} prompts across ${VAULT.tracks} tracks…`}
                className="flex-1 bg-transparent text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                data-testid="command-palette-input"
              />
              {loading && <Loader2 size={14} className="animate-spin text-purple-400" />}
              <kbd className="hidden sm:inline text-[10px] tracking-widest text-zinc-500 border border-zinc-800 px-1.5 py-0.5">
                ESC
              </kbd>
            </div>
            <div
              ref={listRef}
              className="max-h-[420px] overflow-y-auto"
              data-testid="command-palette-results"
            >
              {query.trim().length < 2 && (
                <EmptyState message="Type at least 2 characters to search" />
              )}
              {query.trim().length >= 2 && !loading && results.length === 0 && (
                <EmptyState message={`No prompts matched "${query.trim()}"`} />
              )}
              {results.map((r, i) => (
                <ResultRow
                  key={`${r.track_slug}-${r.prompt_index}`}
                  r={r}
                  active={i === active}
                  onHover={() => setActive(i)}
                  onClick={() => goToResult(r)}
                  idx={i}
                />
              ))}
            </div>
            <div className="flex items-center justify-between px-4 py-2 border-t border-zinc-900 text-[10px] tracking-widest uppercase text-zinc-600">
              <span className="flex items-center gap-3">
                <span>↑↓ move</span>
                <span>↵ open</span>
              </span>
              <span>{results.length ? `${results.length} results` : ""}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResultRow({ r, active, onHover, onClick, idx }) {
  const isElite = r.kind === "elite";
  return (
    <button
      type="button"
      data-idx={idx}
      onMouseEnter={onHover}
      onClick={onClick}
      className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-zinc-900 transition-colors ${
        active ? "bg-purple-500/[0.08]" : "hover:bg-zinc-900/40"
      }`}
      data-testid={`command-palette-result-${idx}`}
    >
      <div className="mt-0.5 shrink-0">
        {isElite ? (
          <Crown size={14} className="text-purple-400" />
        ) : r.locked ? (
          <Lock size={14} className="text-zinc-600" />
        ) : (
          <ArrowRight size={14} className="text-purple-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span
            className={`text-[9px] tracking-[0.2em] uppercase ${
              isElite ? "text-purple-300" : r.locked ? "text-zinc-600" : "text-zinc-500"
            }`}
          >
            {isElite ? "Elite" : r.track_name}
          </span>
          {r.inner_circle && !r.locked && (
            <span className="text-[9px] tracking-[0.2em] uppercase text-amber-400">Inner circle</span>
          )}
          {r.locked && (
            <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-600">locked</span>
          )}
        </div>
        <div className="text-sm text-zinc-100 leading-snug">{r.prompt_title}</div>
        {r.snippet && !r.locked && (
          <div className="text-[11px] text-zinc-500 leading-snug mt-1 line-clamp-2">{r.snippet}</div>
        )}
      </div>
    </button>
  );
}

function EmptyState({ message }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-xs tracking-[0.2em] uppercase text-zinc-600">{message}</div>
    </div>
  );
}

function PaletteHint({ onOpen }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("stagvault_palette_hint_dismissed") === "1";
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  function dismiss(e) {
    e.stopPropagation();
    try {
      localStorage.setItem("stagvault_palette_hint_dismissed", "1");
    } catch {
      /* no-op */
    }
    setDismissed(true);
  }
  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      className="group fixed bottom-5 right-5 z-40 hidden md:flex items-center gap-3 bg-gradient-to-br from-[#0d0913] via-[#0a070d] to-[#0d0805] border border-purple-500/50 hover:border-purple-400 pl-4 pr-2 py-2.5 min-w-[300px] cursor-pointer transition-all duration-300 shadow-[0_0_28px_-4px_rgba(168,85,247,0.55),0_0_14px_-2px_rgba(176,112,255,0.35)] hover:shadow-[0_0_44px_-2px_rgba(168,85,247,0.75),0_0_22px_-2px_rgba(176,112,255,0.55)]"
      data-testid="command-palette-hint"
      style={{
        // Layered gold+purple animated border effect via dual outline
        outline: "1px solid rgba(176, 112, 255, 0.25)",
        outlineOffset: "-3px",
      }}
    >
      <SearchIcon size={16} className="text-purple-300 group-hover:text-purple-200 shrink-0 transition-colors" />
      <span className="flex-1 text-zinc-300 group-hover:text-white text-[13px] tracking-wide transition-colors">
        Search{" "}
        <span className="text-amber-400/90 group-hover:text-amber-300 font-semibold transition-colors">{VAULT.promptsFmt} prompts</span>
        …
      </span>
      <kbd className="inline-flex items-center gap-1 px-2 py-1 bg-black/60 border border-purple-500/40 text-purple-200 text-[10px] tracking-[0.15em] uppercase font-mono shrink-0">
        <CmdIcon size={10} />K
      </kbd>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 text-zinc-600 hover:text-zinc-300 transition-colors shrink-0"
        aria-label="Dismiss search hint"
        data-testid="command-palette-hint-dismiss"
      >
        ×
      </button>
    </div>
  );
}
